import React from "react";

export interface SelectMenuOption<T = string> {
  value: T;
  label: string;
}

export interface SelectMenuProps<T = string> {
  /**
   * Array of options to display in the menu
   */
  options: SelectMenuOption<T>[];
  /**
   * Currently selected value (used to highlight active option)
   */
  selectedValue?: T;
  /**
   * Callback when an option is selected
   */
  onSelect: (value: T) => void;
  /**
   * Optional function to determine if an option is active
   * If not provided, compares option.value === selectedValue
   */
  isOptionActive?: (option: SelectMenuOption<T>, selectedValue?: T) => boolean;
  /**
   * Minimum width of the menu
   */
  minWidth?: string;
  /**
   * Additional CSS classes for the menu container
   */
  className?: string;
}

/**
 * Simple single-select dropdown menu component.
 * Displays a list of options with active state highlighting.
 */
export function SelectMenu<T = string>({
  options,
  selectedValue,
  onSelect,
  isOptionActive,
  minWidth = "200px",
  className = "",
}: SelectMenuProps<T>) {
  const defaultIsActive = (option: SelectMenuOption<T>, selected?: T) => {
    return option.value === selected;
  };

  const checkIsActive = isOptionActive || defaultIsActive;

  const baseClasses =
    "absolute bg-card border border-border rounded-lg shadow-lg p-2 z-10 mt-1";
  const menuClasses = `${baseClasses} ${className}`.trim();

  return (
    <div className={menuClasses} style={{ minWidth }}>
      {options.map((option) => {
        const isActive = checkIsActive(option, selectedValue);
        return (
          <button
            key={String(option.value)}
            onClick={() => onSelect(option.value)}
            className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-accent ${
              isActive
                ? "bg-primary/10 text-primary font-medium"
                : "text-muted-foreground"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
